import type { Product, SourceSystem } from 'eds-common';
import { type RedeemedAccessCode } from 'eds-common/api/v1';
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import { getInitialStepId, stepConfigs, type StepId } from './step-configs';
import type {
  AccessCodeRedemptionInitialState,
  PendingAppLaunch,
} from './access-code-deep-link';

export interface RedemptionData {
  product: Product | null;
  redeemedAccessCode: RedeemedAccessCode | null;
  accessCode: string;
  confirmedOrganization: boolean | null;
  selectedClassIds: string[];
}

const initialRedemptionData: RedemptionData = {
  product: null,
  redeemedAccessCode: null,
  accessCode: '',
  confirmedOrganization: null,
  selectedClassIds: [],
};

type RedemptionAction =
  | { type: 'UPDATE'; updates: Partial<RedemptionData> }
  | { type: 'RESET' };

function redemptionDataReducer(
  state: RedemptionData,
  action: RedemptionAction
): RedemptionData {
  switch (action.type) {
    case 'UPDATE':
      return { ...state, ...action.updates };

    case 'RESET':
      return initialRedemptionData;

    default:
      return state;
  }
}

interface AccessCodeRedemptionContextType {
  currentStepId: StepId;
  goToNextStep: () => void;
  isDialogOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
  redemptionData: RedemptionData;
  updateRedemptionData: (updates: Partial<RedemptionData>) => void;
  pendingAppLaunch: PendingAppLaunch | null;
}

const AccessCodeRedemptionContext =
  createContext<AccessCodeRedemptionContextType | null>(null);

interface AccessCodeRedemptionProviderProps {
  children: ReactNode;
  userSource: SourceSystem;
  isFirstTime: boolean;
  externalOpen?: boolean;
  externalOnOpenChange?: (open: boolean) => void;
  initialState?: AccessCodeRedemptionInitialState | null;
  pendingAppLaunch?: PendingAppLaunch | null;
}

export function AccessCodeRedemptionProvider({
  children,
  userSource,
  isFirstTime,
  externalOpen,
  externalOnOpenChange,
  initialState,
  pendingAppLaunch = null,
}: AccessCodeRedemptionProviderProps) {
  const [redemptionData, dispatch] = useReducer(redemptionDataReducer, {
    ...initialRedemptionData,
    accessCode: initialState?.accessCode ?? initialRedemptionData.accessCode,
    product: initialState?.product ?? initialRedemptionData.product,
  });

  const [currentStepId, setCurrentStepId] = useState(() =>
    getInitialStepId({ initialState, isFirstTime })
  );
  const [isDialogOpen, setIsDialogOpen] = useState(Boolean(initialState));

  const goToNextStep = useCallback(() => {
    const nextId = stepConfigs[currentStepId].next({ userSource, isFirstTime });
    if (nextId) {
      setCurrentStepId(nextId);
    }
  }, [currentStepId, userSource, isFirstTime]);

  const updateRedemptionData = useCallback(
    (updates: Partial<RedemptionData>) => {
      dispatch({ type: 'UPDATE', updates });
    },
    []
  );

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      setIsDialogOpen(open);

      if (externalOnOpenChange) {
        externalOnOpenChange(open);
      }

      if (!open) {
        // Reset to the entry step so the next open starts fresh.
        setCurrentStepId(getInitialStepId({ initialState: null, isFirstTime }));
        dispatch({ type: 'RESET' });
      }
    },
    [externalOnOpenChange, isFirstTime]
  );

  const contextValue = useMemo(
    () => ({
      currentStepId,
      goToNextStep,
      isDialogOpen: externalOpen !== undefined ? externalOpen : isDialogOpen,
      setIsDialogOpen: handleDialogOpenChange,
      redemptionData,
      updateRedemptionData,
      pendingAppLaunch,
    }),
    [
      currentStepId,
      goToNextStep,
      externalOpen,
      isDialogOpen,
      handleDialogOpenChange,
      redemptionData,
      updateRedemptionData,
      pendingAppLaunch,
    ]
  );

  return (
    <AccessCodeRedemptionContext.Provider value={contextValue}>
      {children}
    </AccessCodeRedemptionContext.Provider>
  );
}

export function useAccessCodeRedemptionContext() {
  const context = useContext(AccessCodeRedemptionContext);
  if (!context) {
    throw new Error(
      'useAccessCodeRedemptionContext must be used within a AccessCodeRedemptionProvider'
    );
  }
  return context;
}
